import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import PropTypes from 'prop-types';
import moment from 'moment';
import { isDesktop, isMobile } from '../utils.js';
import {
    PrimaryButton,
    Documents,
    LoadingBar,
    BreadCrumbs,
    SafetyClassBox,
    ReweighModal,
    DecommissionModal,
    SystemRegistrationModal,
    Error500,
    ActionBox,
    GlobalWarmingPotential,
    Tabs,
    ChildTabs,
    PageTitleWithToolTip,
    Error404,
    DeletedRecord
} from './Common';
import InventoryHeader from './Inventory/InventoryHeader';
import ServiceEventTable from "./Inventory/Tables/ServiceEventTable.js";
import CylinderReweighTable from "./Inventory/Tables/CylinderReweighTable.js";
import {
    Grid,
    IconButton,
    Stack,
    Chip,
    Tooltip,
    Box
} from '@mui/material';
import Routes from '../routes';
import Enums from '../enums';
import { Pill } from './Common/Pill';
import CloseIcon from '@mui/icons-material/Close';
import ScaleOutlinedIcon from '@mui/icons-material/ScaleOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import UserInfo from '../UserInfo';
import { hotjar } from 'react-hotjar';
import AssetMovementTable from './Inventory/Tables/AssetMovementTable';
import MaterialTransferTable from './Inventory/Tables/MaterialTransferTable';
import RecoverCylinderStatusHistoryTable from "./Inventory/Tables/RecoveryCylinderStatusHistoryTable";
import AliasFormModal from './Inventory/AliasFormModal.js';
import SystemMapTable from './Inventory/Tables/SystemMapTable.js';
import SystemRegistrationTable from './Inventory/Tables/SystemRegistrationTable.js';
import { isSubsystem } from './AddAsset/utils.js';
import { CommentBox } from '../Icons/Icons';
import Discussion from './Inventory/Discussion.js';
import RelatedAssetsTable from './Inventory/Tables/RelatedAssetsTable.js';
import { Grey1, Grey3 } from '../color_utils.js';
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded';
import AddLinkIcon from '@mui/icons-material/AddLink';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined';
import AppRegistrationOutlinedIcon from '@mui/icons-material/AppRegistrationOutlined';
import AddSensorModal from './Inventory/AddSensorModal.js';
import { useNavigate, useBreadCrumbs } from '../hooks';
import CommonDialog from './Common/CommonDialog.js';
import AssetDetailsCylinderGasTransfer from './AssetDetailsCylinderGasTransfer.js';
import { getChartColor } from '../Utils/charts_utils';

const initialAliasModalValue = {
    isVisible: false,
    alias: null
};

const AssetDetails = ({
    history = {},
    history: {
        location: {
            state: {
                from,
                activeTab,
                secondaryActiveTab,
                initialCommentId,
                initialSubsystemRow,
                activeRowState,
            } = {}
        } = {}
    } = {},
    match: {
        params: { id: assetID = '' },
    },
}) => {
    const [errorLoading, setErrorLoading] = useState(null);
    const [isAssetNotFound, setIsAssetNotFound] = useState(false);
    const [id, setId] = useState(null);
    const [name, setName] = useState('');
    const [assetType, setAssetType] = useState(null);
    const [assetTypeName, setAssetTypeName] = useState(null);
    const [locations, setLocations] = useState([]);
    const [cylinderTypes, setCylinderTypes] = useState([]);
    const [materialTypes, setMaterialTypes] = useState([]);
    const [materialStatuses, setMaterialStatuses] = useState([]);
    const [psiRatings, setPsiRatings] = useState([]);
    const [assetStatusName, setAssetStatusName] = useState(null);
    const [systemStatus, setSystemStatus] = useState(null)
    const [initialData, setInitialData] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isCylinder, setIsCylinder] = useState(false);
    const [activeTabPrimary, setActiveTabPrimary] = useState(1);
    const [activeTabSecondary, setActiveTabSecondary] = useState(1)
    const [previousTab, setPreviousTab] = useState({});
    const [activeRow, setActiveRow] = useState({});
    const [safetyClass, setSafetyClass] = useState(null)
    const [sublocation, setSublocation] = useState("")
    const [safetyClassConfig, setSafetyClassConfig] = useState(null);
    const [headerCollapsed, setHeaderCollapsed] = useState(true);
    const [globalWarmingPotential, setGlobalWarmingPotential] = useState(0);
    const [IsEnabled, setIsEnabled] = useState(false);
    const [assetIdentities, setAssetIdentities] = useState([]);
    const [reweighState, setAllReweighState] = useState({
        open: false,
        reweighDate: '',
        newGasWeightLbs: '',
        reasonId: '',
        notes: '',
        saving: false,
    });
    const [decommissionState, setAllDecommissionState] = useState({
        open: false,
        reasonId: '',
        decommissionDate: '',
        saving: false,
    });
    const [openAliasModal, setOpenAliasModal] = useState(initialAliasModalValue);
    const [regulatedAssetTypes, setRegulatedAssetTypes] = useState([]);
    const [isFocusText, setIsFocusText] = useState(false);
    const [relatedAssetsfilterOptions, setRelatedAssetsfilterOptions] = useState([]);
    const [openSensorModal, setOpenSensorModal] = useState(false);
    const [cylinderTransferTableRefreshToggle, setCylinderTransferTableRefreshToggle] = useState(crypto.randomUUID());
    const [showCylinderTransferDialog, setShowCylinderTransferDialog] = useState(false);
    const [systemRegistrationState, setAllSystemRegistrationState] = useState({
        open: false,
        editId: null,
        registrationType: '',
        systemId: '',
        effectiveDate: '',
        endDate: '',
        notes: '',
        saving: false,
    });
    const [systemRegistrationRefresh, setSystemRegistrationRefresh] = useState(false);

    const navigate = useNavigate({
        pageName: 'AssetDetails'
    });
    const breadCrumbs = useBreadCrumbs({
        path: { id: 'AssetDetails', text: `Inventory #${assetID}`, link: history.location.pathname }
    });

    const isRecoveryCylinder = data && data.assetType === Enums.AssetTypes.RecoveryCylinder;
    const isVirginCylinder = data && data.assetType === Enums.AssetTypes.VirginCylinder;
    const renderCylinderReweigh = !UserInfo.isReadOnly() && (isRecoveryCylinder || isVirginCylinder);

    const isLeakMonitoringDevice = data && data.assetType === Enums.AssetTypes.LeakMonitoringDevice;
    const renderLeakMonitoringDeviceMenu = (UserInfo.isNativeAdminRole() || UserInfo.isNativeRegionalAdministrator()) && isLeakMonitoringDevice;
    const renderTransferGasToCylinder = renderCylinderReweigh && data && data.currentGasWeightLBS > 0 && data.cylinderStatusName == "Active";

    const isCoolingAppliance = data && (
        data.assetType === Enums.AssetTypes.Refrigeration
        || data.assetType === Enums.AssetTypes.ComfortCooling
        || data.assetType === Enums.AssetTypes.ProcessCooling
    );
    const canDecommission = isCoolingAppliance
        && data && data.assetStatusName === "Inactive"
        && data.systemStatusName !== "Retrofitted"
        && data.systemStatusName !== "Moved";
    const hasDecommissionPermission = UserInfo.isSystemAdministrator()
        || (UserInfo.isNativeUser() && UserInfo.isSystemOwner()
            && (UserInfo.isSuperTenantAdmin()
                || UserInfo.isTenantAdministrator()
                || UserInfo.isRegionalAdministrator()));
    const renderDecommission = canDecommission && hasDecommissionPermission;

    const canEditAsset = UserInfo.isSystemAdministrator()
        || (UserInfo.isNativeUser() && (UserInfo.isAdminRole() || UserInfo.isRegionalAdministrator()) && CanEdit());

    const actioItemsInit = () => {
        const items = [];

        if (canEditAsset) {
            items.push({
                Icon: EditOutlinedIcon,
                title: "Edit",
                onClick: () => editAssets()
            });
        }

        if (renderCylinderReweigh) {
            items.push({
                Icon: ScaleOutlinedIcon,
                title: "Cylinder Reweigh",
                onClick: () => setReweighState({ open: true })
            })
        }


        if (UserInfo.isNativeAdminRole() || UserInfo.isSystemAdministrator()) {
            items.push({
                Icon: ContentCopyOutlinedIcon,
                title: "Add Alias / External ID",
                onClick: () => onAliasModalStateChange()
            });
        }

        items.push({
            Icon: CommentBox,
            title: "Add New Comment",
            onClick: () => {
                //if have systemMap then go to index 6 else 5
                setIsFocusText(true);
                setActiveTabPrimary((!isSubsystem(assetType) && data) ? 6 : 5)
            }
        });

        if (renderLeakMonitoringDeviceMenu) {
            items.push({
                Icon: SensorsRoundedIcon,
                title: "Add Sensor",
                onClick: () => { setOpenSensorModal(true) }
            });
            items.push({
                Icon: AddLinkIcon,
                title: "Link Cooling Appliances",
                onClick: () => editLinkCoolingAppliances()
            });
        }

        if (renderTransferGasToCylinder) {
            items.push({
                Icon: CompareArrowsIcon,
                title: "Transfer Gas to Another Cylinder",
                onClick: () => setShowCylinderTransferDialog(true)
            });
        }

        if (renderDecommission) {
            items.push({
                Icon: PowerSettingsNewOutlinedIcon,
                title: "Decommission Appliance",
                onClick: () => setDecommissionState({ open: true })
            });
        }

        items.push({
            Icon: AppRegistrationOutlinedIcon,
            title: "System Registration",
            onClick: () => setSystemRegistrationState({ open: true })
        });

        return items;
    };

    const actionItems = actioItemsInit().length > 0 ? actioItemsInit() : [];

    const pwaActionItems = actionItems.filter(item =>
        item.title === "Cylinder Reweigh" || item.title === "Add Alias / External ID" || item.title === "Transfer Gas to Another Cylinder" || item.title === "Decommission Appliance"
    );

    const reweighModalButtonDisabled = (
        !reweighState.reweighDate
        || (reweighState.newGasWeightLbs < 0 || reweighState.newGasWeightLbs === null || reweighState.newGasWeightLbs === undefined || reweighState.newGasWeightLbs === "")
        || !reweighState.reasonId
        || reweighState.saving
    );

    const fetchData = async () => {

        try {
            const response = await fetch(Routes.Asset.Details(assetID));
            const data = await response.json();

            const regulatedAssetTypeResponse = await fetch(Routes.RegulatedAssetType.GetAll());
            const regulatedAssetTypeData = await regulatedAssetTypeResponse.json();

            if (data.status === 404) {
                setIsAssetNotFound(true);
                setLoading(false);
                return;
            }

            if (UserInfo.isRegionalAdministrator()
                && !UserInfo.canServiceLocation(data.location, data.instanceId)) {
                setIsAssetNotFound(true);
                setLoading(false);
                return;
            }

            if (data.errors) {
                setErrorLoading(true);
                setLoading(false);
                return;
            }

            const {
                dropdowns: {
                    locations = [],
                    cylinderTypes = [],
                    materialTypes = [],
                    materialStatuses = [],
                    psiRatings = []
                } = {},
                assetType = '',
                name = '',
                globalWarmingPotential = 0,
            } = data;

            setName(name);
            setAssetStatusName(data.assetStatusName);
            setSystemStatus(data.systemStatusName);
            setLocations(locations);
            setCylinderTypes(cylinderTypes);
            setMaterialTypes(materialTypes);
            setMaterialStatuses(materialStatuses);
            setPsiRatings(psiRatings);
            setInitialData(data);
            setSublocation(data.locationOnSite || '');
            setData(data);
            setAssetType(assetType);
            setAssetTypeName(data.assetTypeName)
            setIsCylinder(checkCylinder(assetType));
            setSafetyClass(data.safetyClassName || null);
            setIsEnabled(data.enabled)
            setGlobalWarmingPotential(globalWarmingPotential);
            if (data.safetyClassName != null) {
                setSafetyClassConfig(GetSafetyConfig(data.safetyClassName))
            }

            const identityValues = data.assetIdentities.filter(x => x.enabled === true).map(({ id, identityValue, assetIdentityTypeId }) => ({
                id,
                identityValue,
                assetIdentityTypeId
            }));
            setAssetIdentities(identityValues)

            if (regulatedAssetTypeData.length) {
                const regulatedAssetTypesIds = data.regulatedAssetTypes ? data.regulatedAssetTypes.map(x => x.id) : [];
                const selectedRegulatedAssetTypes = regulatedAssetTypeData.map(x => ({
                    name: x.name,
                    isSelected: regulatedAssetTypesIds.includes(x.id),
                    tooltip: x.description || "",
                    color: getChartColor(x.name)
                }));

                setRegulatedAssetTypes(selectedRegulatedAssetTypes);
            }

            setLoading(false);
        } catch (error) {
            console.log(error);
            setErrorLoading(true);
            setLoading(false);
        }
    };

    function CanEdit() {
        if (!data) return false;

        const EditableAssets = [
            Enums.AssetTypes.VirginCylinder,
            Enums.AssetTypes.RecoveryCylinder,
            Enums.AssetTypes.ControlsControl,
            Enums.AssetTypes.CompressorRack,
            Enums.AssetTypes.Evaporator,
            Enums.AssetTypes.Condensor,
            Enums.AssetTypes.Refrigeration,
            Enums.AssetTypes.ComfortCooling,
            Enums.AssetTypes.ProcessCooling,
            Enums.AssetTypes.GasRecoveryCart,
            Enums.AssetTypes.LeakMonitoringDevice,
        ];

        return EditableAssets.includes(data.assetType);
    }

    // Allowed user types (Type = ALL):
    // NOTE: use "native" variants because AssetDetails already scopes instance access.
    const canEditInventory = (
        UserInfo.isSystemAdministrator()
        || (
            UserInfo.isNativeSystemOwner()
            && (
                UserInfo.isSuperTenantAdmin()
                || UserInfo.isTenantAdministrator()
                || UserInfo.isRegionalAdministrator()
            )
        )
    )

    const GetSafetyConfig = (safetyClass) => {
        const A1 = {
            Color1: "#CCDB38",
            Color2: "#FFEB3A",
            TextColor: "#000",
            Text: "A1"
        }
        const A2 = {
            Color1: "#FF9700",
            Color2: "#FFEB3A",
            TextColor: "#FFF",
            Text: "B2"
        }
        const A2L = {
            Color1: "#CCDB38",
            Color2: "#FFEB3A",
            TextColor: "#000",
            Text: "A2L"
        }
        const A3 = {
            Color1: "#F34135",
            Color2: "#CCDB38",
            TextColor: "#FFF",
            Text: "A3"
        }
        const B1 = {
            Color1: "#CCDB38",
            Color2: "#F34135",
            TextColor: "#000",
            Text: "B1"
        }
        const B2 = {
            Color1: "#CCDB38",
            Color2: "#FFEB3A",
            TextColor: "#000",
            Text: "B2"
        }
        const B2L = {
            Color1: "#FFEB3A",
            Color2: "#F34135",
            TextColor: "#000",
            Text: "B2L"
        }
        const B3 = {
            Color1: "#CCDB38",
            Color2: "#FFEB3A",
            TextColor: "#000",
            Text: "B3"
        }

        switch (safetyClass) {
            case "A1":
                return A1;
            case "A2":
                return A2;
            case "A2L":
                return A2L;
            case "A3":
                return A3;
            case "B1":
                return B1;
            case "B2":
                return B2;
            case "B2L":
                return B2L;
            case "B3":
                return B3;
        }
    };

    const checkCylinder = (id) => {
        return (
            id === Enums.AssetTypes.RecoveryCylinder
            || id === Enums.AssetTypes.VirginCylinder
            || id === Enums.AssetTypes.Bottle
        )
    };

    const editAssets = () => {
        navigate.push("/EditInventory/" + id);
    };

    const editLinkCoolingAppliances = () => {
        navigate.push("/LinkCoolingAppliances/" + id);
    };

    const setReweighState = (new_reweigh_obj) => (
        setAllReweighState((prevState) => (
            { ...prevState, ...new_reweigh_obj }
        ))
    );

    const resetReweighState = () => (
        setReweighState({
            open: false,
            reweighDate: '',
            newGasWeightLbs: '',
            reasonId: '',
            notes: '',
            saving: false,
        })
    );

    const handleReweighSubmit = async () => {
        const { currentGasWeightLBS = null } = data;
        const reweighData = {
            oldGasWeightLbs: currentGasWeightLBS,
            newGasWeightLbs: reweighState.newGasWeightLbs,
            cylinderId: id,
            notes: reweighState.notes,
            cylinderReweighReasonId: reweighState.reasonId,
            reweighDate: moment(reweighState.reweighDate).format('YYYY-MM-DD HH:mm:ss'),
        };

        const submitData = async () => {
            setReweighState({ saving: true });
            const response = await fetch(Routes.Asset.CreateReweigh(), {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(reweighData)
            });
            const data = await response.json();
            console.log('reweigh data RESPONSE', data);

            if (response.status === 200) {
                setReweighState({ saving: false, open: false });
                toast.success('Cylinder has been successfully reweighed.', { duration: 6000 });
                await fetchData();
                return resetReweighState();
            } else {
                resetReweighState();
                return toast.error('Something went wrong. Cylinder could not be reweighed.', { duration: 6000 });
            }
        };

        await submitData();
    };

    const setDecommissionState = (new_obj) => (
        setAllDecommissionState((prevState) => ({ ...prevState, ...new_obj }))
    );

    const resetDecommissionState = () => (
        setDecommissionState({
            open: false,
            reasonId: '',
            decommissionDate: '',
            saving: false,
        })
    );

    const decommissionButtonDisabled = (
        !decommissionState.reasonId
        || !decommissionState.decommissionDate
        || decommissionState.saving
    );

    const handleDecommissionSubmit = async () => {
        const decommissionData = {
            assetId: parseInt(assetID),
            coolingApplianceStatusId: decommissionState.reasonId,
            decommissionDate: moment(decommissionState.decommissionDate).format('YYYY-MM-DD'),
        };

        setDecommissionState({ saving: true });
        const response = await fetch(Routes.Asset.DecommissionAsset(), {
            method: "POST",
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(decommissionData)
        });

        if (response.status === 200) {
            setDecommissionState({ saving: false, open: false });
            toast.success('Appliance has been successfully decommissioned.', { duration: 6000 });
            await fetchData();
            return resetDecommissionState();
        } else {
            resetDecommissionState();
            return toast.error('Something went wrong. Appliance could not be decommissioned.', { duration: 6000 });
        }
    };

    const setSystemRegistrationState = (new_obj) => (
        setAllSystemRegistrationState((prevState) => ({ ...prevState, ...new_obj }))
    );

    const resetSystemRegistrationState = () => (
        setSystemRegistrationState({
            open: false,
            editId: null,
            registrationType: '',
            systemId: '',
            effectiveDate: '',
            endDate: '',
            notes: '',
            saving: false,
        })
    );

    const handleSystemRegistrationEdit = (row) => {
        setSystemRegistrationState({
            open: true,
            editId: row.id,
            registrationType: row.registrationTypeId,
            systemId: row.systemId,
            effectiveDate: row.effectiveDate || '',
            endDate: row.endDate || '',
            notes: row.notes || '',
        });
    };

    const handleSystemRegistrationSubmit = async () => {
        const isEdit = !!systemRegistrationState.editId;
        const registrationData = {
            ...(isEdit && { id: systemRegistrationState.editId }),
            assetId: parseInt(assetID),
            registrationTypeId: systemRegistrationState.registrationType,
            systemId: systemRegistrationState.systemId,
            effectiveDate: systemRegistrationState.effectiveDate
                ? moment(systemRegistrationState.effectiveDate).format('YYYY-MM-DD')
                : null,
            endDate: systemRegistrationState.endDate
                ? moment(systemRegistrationState.endDate).format('YYYY-MM-DD')
                : null,
            notes: systemRegistrationState.notes,
        };

        setSystemRegistrationState({ saving: true });
        const url = isEdit
            ? Routes.SystemRegistration.Update()
            : Routes.SystemRegistration.Create();
        const response = await fetch(url, {
            method: isEdit ? "PUT" : "POST",
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(registrationData)
        });

        if (response.status === 200) {
            setSystemRegistrationState({ saving: false, open: false });
            toast.success(`System registration has been successfully ${isEdit ? 'updated' : 'saved'}.`, { duration: 6000 });
            setSystemRegistrationRefresh(prev => !prev);
            await fetchData();
            return resetSystemRegistrationState();
        } else {
            resetSystemRegistrationState();
            return toast.error(`Something went wrong. System registration could not be ${isEdit ? 'updated' : 'saved'}.`, { duration: 6000 });
        }
    };

    const XButton = () => {
        // reset tab local data
        navigate.goBack('/Inventory', from);
    }

    const onAliasModalStateChange = (alias) => {
        setOpenAliasModal({
            isVisible: !openAliasModal.isVisible,
            alias: alias ? alias : null
        });
    }

    const onAliasTransactionComplete = (newData) => {
        const newAssetIdentities = [...assetIdentities];
        const index = newAssetIdentities.findIndex((x) => x.id === newData.id);
        let flagMessage = "";

        if (index < 0) {
            flagMessage = "The Alias / External ID has been successfully created";
            newAssetIdentities.push({
                id: newData.id,
                identityValue: newData.identityValue,
                assetIdentityTypeId: newData.assetIdentityTypeId,
            });
        } else {
            const assetIdentity = newAssetIdentities[index];
            flagMessage = "The Alias / External ID has been successfully updated";
            assetIdentity.identityValue = newData.identityValue;
            assetIdentity.assetIdentityTypeId = newData.assetIdentityTypeId;
        }


        setAssetIdentities(newAssetIdentities);
        toast.success(flagMessage, { duration: 6000 });
    }

    const onAliasDeleteComplete = (id) => {
        const newAssetIdentities = [...assetIdentities];
        const index = newAssetIdentities.findIndex((x) => x.id === id);

        if (index > -1) {
            newAssetIdentities.splice(index, 1);
        }

        setAssetIdentities(newAssetIdentities);
        toast.success('The Alias / External ID has been successfully deleted.', { duration: 6000 });
    };

    const fetchRelatedAssetsfilterOptions = async () => {
        const response = await fetch(Routes.Asset.GetRelatedAssetsTableLookups());
        const data = await response.json();
        setRelatedAssetsfilterOptions(data);
    }

    useEffect(() => {
        setLoading(true);
        setId(assetID);

        fetchData();
        fetchRelatedAssetsfilterOptions();
        hotjar.stateChange(document.URL + '#History');
    }, [assetID]);

    useEffect(() => {
        if (previousTab["activeTabPrimary"] !== undefined && activeTab === undefined) {
            setActiveTabPrimary(previousTab["activeTabPrimary"]);
            setActiveTabSecondary(previousTab["activeTabSecondary"]);
        }
        setPreviousTab({
            "activeTabPrimary": activeTabPrimary,
            "activeTabSecondary": activeTabSecondary,
        });

        if (activeTab) {
            setActiveTabPrimary(activeTab);
        }

        // For example: 
        // {baseUrl}/Inventory/{assetId}#5, We can see that 5 is the number of the active tab
        // So when user navigate back and forth, we use this value to define current active tab
        const storedTab = window.location.href.split('#')[1];

        if (secondaryActiveTab) {
            setActiveTabSecondary(secondaryTabs.label.indexOf(secondaryActiveTab));
        } else if (storedTab) {
            setActiveTabPrimary(parseInt(storedTab));
        }

        if (activeRowState) {
            setActiveRow(activeRowState);
        }
    }, []);

    const aliases = useMemo(() => {
        return assetIdentities ? assetIdentities.map((x, index) => (<span key={`alias-${index}`} onClick={UserInfo.isNativeAdminRole() || UserInfo.isSystemAdministrator() ? () => onAliasModalStateChange(x) : null}>{x.identityValue}</span>)) : [];
    }, [assetIdentities]);

    const selectedRegulatedAssetTypes = useMemo(() => {
        if (!regulatedAssetTypes.length) return [];

        return regulatedAssetTypes.some(x => x.isSelected) && regulatedAssetTypes.filter(x => x.isSelected);
    }, [regulatedAssetTypes]);

    const tabs = {
        label: [],
        children: []
    };
    const secondaryTabs = {
        label: [],
        children: []
    };

    const primaryTabsMemo = useMemo(() => {
        secondaryTabs.label.push("Service Events");
        secondaryTabs.children.push(
            <Grid container>
                <Grid item xs={12} md={12} lg={12} pt={2} paddingInline={{ xs: 0, lg: 2 }}>
                    <ServiceEventTable
                        assetID={id}
                        isCylinder={isVirginCylinder || isRecoveryCylinder}
                    />
                </Grid>
            </Grid>
        );

        if (isRecoveryCylinder || isVirginCylinder) {
            secondaryTabs.label.push('Cylinder Transfers');
            secondaryTabs.children.push(
                <Grid container mt={{ xs: 0, lg: 4 }}>
                    <Grid item xs={12} md={12} lg={12}>
                        <RecoverCylinderStatusHistoryTable
                            assetID={id}
                            title='Cylinder Transfers'
                            refreshToggle={cylinderTransferTableRefreshToggle}
                        />
                    </Grid>
                </Grid>
            );
        }

        secondaryTabs.label.push("Material Transfers");
        secondaryTabs.children.push(
            <Grid container mt={{ xs: 0, lg: 4 }}>
                <Grid item xs={12} md={12} lg={12} pt={2} paddingInline={{ xs: 0, lg: 2 }}>
                    <MaterialTransferTable assetID={id} />
                </Grid>
            </Grid>
        );

        secondaryTabs.label.push("Movements");
        secondaryTabs.children.push(
            <Grid container mt={{ xs: 0, lg: 4 }}>
                <Grid item xs={12} md={12} lg={12} pt={2} paddingInline={{ xs: 0, lg: 2 }}>
                    <AssetMovementTable assetID={id} />
                </Grid>
            </Grid>
        );

        if (isRecoveryCylinder || isVirginCylinder) {
            secondaryTabs.label.push("Cylinder Reweigh");
            secondaryTabs.children.push(
                <CylinderReweighTable
                    assetID={id}
                    noDataButtonOnClick={() => setReweighState({ open: true })}
                />
            );
        }

        tabs.label.push("History");
        tabs.children.push(
            <ChildTabs
                activeTab={activeTabSecondary}
                onhandleChange={(_, newValue) => {
                    console.log(newValue, secondaryTabs.label);
                    setActiveTabSecondary(newValue);
                    hotjar.stateChange(document.URL + '#' + secondaryTabs.label[newValue - 1]);
                }}
                labels={secondaryTabs.label}
                tabElements={secondaryTabs.children}
            />
        );

        if (isDesktop) {
            tabs.label.push("Documents");
            tabs.children.push(
                <Grid
                    container
                    direction="row"
                    alignItems="center"
                    style={{ marginBottom: '2%' }}
                >
                    <Grid item xs={12} lg={12} mb={1} pt={2}>
                        <Documents id={id} fileType={14} showAddButton />
                    </Grid>
                </Grid>
            );

            tabs.label.push("Related Assets");
            tabs.children.push(
                <Grid
                    container
                    direction="row"
                    alignItems="center"
                    style={{ marginBottom: '2%' }}
                >
                    <Grid item xs={12} lg={12} mb={1} pt={2}>
                        <RelatedAssetsTable assetId={id} filterOptions={relatedAssetsfilterOptions} />
                    </Grid>
                </Grid>
            );

            tabs.label.push("System Registration");
            tabs.children.push(
                <Grid
                    container
                    direction="row"
                    alignItems="center"
                    style={{ marginBottom: '2%' }}
                >
                    <Grid item xs={12} lg={12} mb={1} pt={2}>
                        <SystemRegistrationTable
                            assetId={id}
                            refresh={systemRegistrationRefresh}
                            handleEdit={handleSystemRegistrationEdit}
                        />
                    </Grid>
                </Grid>
            );

            if (!isSubsystem(assetType) && data) {
                tabs.label.push("System Map");
                tabs.children.push(
                    <SystemMapTable
                        assetID={id}
                        assetName={name}
                        assetLocationID={data.location}
                        activeTab={activeTabPrimary}
                        initialSubsystemRow={initialSubsystemRow}
                        activeRow={activeRow}
                        setActiveRow={setActiveRow}
                    />
                );
            }

            tabs.label.push("Discussion");
            tabs.children.push(
                <Discussion
                    itemID={id}
                    type={Enums.CommentType.Asset}
                    initialCommentId={initialCommentId}
                    isFocusText={isFocusText}
                />
            );
        }
        return tabs;
    }, [assetID, activeTabSecondary, data, activeTabPrimary]);

    if (loading) {
        return <LoadingBar />;
    }

    if (errorLoading) {
        return <Error500 />;
    }

    if (isAssetNotFound) {
        return <Error404/>;
    }
    if (!IsEnabled) {
        return (
            <DeletedRecord
                recordType="Asset"
                recordId={id}
                backUrl="/Inventory"
                backLabel="Back to Inventory"
            />
        );
    }

    return (
        <>
            <BreadCrumbs
                path={breadCrumbs.getSortedBreadCrumbs()}
                sx={{ display: { xs: 'none', lg: 'flex' } }}
            />

            <Grid container direction="row" alignItems={{ xs: "start", md: "center" }} sx={{ marginBottom: '1%', pb: { xs: 2, md: 0 }, pt: { xs: 3, md: 0 } }}>
                <Grid item xs={1} md={'auto'}>
                    <IconButton aria-label="close" onClick={() => XButton()}>
                        <CloseIcon />
                    </IconButton>
                </Grid>
                <Grid item xs={11} md={'auto'}>
                    <PageTitleWithToolTip
                        pageTitle={`${name}`}
                    />
                </Grid>
                <Grid item sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'row', pt: { xs: 2, md: 0 } }}>
                    <Pill text={assetTypeName} type="TooltipBlue" bold={600} />
                </Grid>
                {systemStatus && (
                    <Grid item sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'row', pt: { xs: 2, md: 0 } }}>
                        <Pill text={systemStatus} type='TooltipBlueBorderOnly' bold={600} />
                    </Grid>
                )}
                {/* Story for Inventory Print & Inventory export coming in the future */}
                {/* <Grid item className={classes.leftAlign}>
                    {(localStorage.getItem("isprod") === 'false') && (
                        <IconButton>
                            <img
                                src="/images/ic_print_outline.svg"
                                alt="Print"
                            />
                        </IconButton>
                    )}
                </Grid>
                <Grid item className={classes.leftAlign2}>
                    {(localStorage.getItem("isprod") === 'false') && (
                        <IconButton>
                            <PublishIcon />
                        </IconButton>
                    )}
                </Grid> */}
                {((UserInfo.isSystemAdministrator() || (UserInfo.isNativeUser() && (UserInfo.isAdminRole() || UserInfo.isRegionalAdministrator()) && CanEdit())) || canEditInventory) && (
                    <Grid
                        item
                        sx={{ marginLeft: 'auto', display: { xs: 'none', md: 'block' } }}
                    >
                        <PrimaryButton buttonText="Edit" onClick={editAssets} />
                    </Grid>
                )}
            </Grid>
            <Box
                sx={{
                    display: { xs: 'flex', md: 'none' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pb: { xs: "16px", md: "0px" },
                    px: 1,
                }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Pill text={assetTypeName} type="TooltipBlue" bold={600} />
                        {systemStatus && <Pill text={systemStatus} type='TooltipBlueBorderOnly' bold={600} />}
                    </Box>
                    {((UserInfo.isSystemAdministrator() || (UserInfo.isNativeUser() && (UserInfo.isAdminRole() || UserInfo.isRegionalAdministrator()) && CanEdit())) || canEditInventory) && (
                        <PrimaryButton buttonText="Edit" onClick={editAssets} />
                    )}

            </Box>

            <Grid
                container
                sx={{ marginBottom: '2%' }}
                justifyContent="flex-end"
                spacing={2}
            >
                {selectedRegulatedAssetTypes.length && (
                    <Grid item xs={12} sx={{ display: { xs: 'none', lg: 'flex' }, flexDirection: 'row' }}>
                        <Stack direction="row" ml={1} spacing={1}>
                            {selectedRegulatedAssetTypes.map(({ tooltip, name, color }) => {
                                const chipProps = {
                                    variant: 'outlined',
                                    label: name,
                                    sx: {
                                        color,
                                        border: `1px solid ${color}`,
                                        fontSize: '12px',
                                        height: '24px',
                                    }
                                };
                                return (
                                    tooltip ? (
                                        <Tooltip arrow title={tooltip}>
                                            <Chip {...chipProps} />
                                        </Tooltip>
                                    ) : (
                                        <Chip {...chipProps} />
                                    )
                                );
                            })}
                        </Stack>
                    </Grid>
                )}
                <Grid item xs={12} md={9} lg={10} sx={{ mx: { xs: 1, md: 0 } }}>
                    <InventoryHeader
                        data={data}
                        aliases={aliases}
                        regulatedAssetTypes={regulatedAssetTypes}
                        setHeaderCollapsed={setHeaderCollapsed}
                        assetType={assetType}
                        pwaActionItemsCount={pwaActionItems.length}
                    />
                    {pwaActionItems.length > 0 && (
                        <Box sx={{ display: { xs: 'block', md: 'none' }, my: 2 }}>
                            <ActionBox items={pwaActionItems} />
                        </Box>
                    )}
                    {(!headerCollapsed || isMobile) && safetyClass && (
                        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                            <SafetyClassBox safetyClass={safetyClass} />
                        </Box>
                    )}
                    {(!headerCollapsed || isMobile) && (
                        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                            <GlobalWarmingPotential
                                marginTop={6}
                                tonsAmount={globalWarmingPotential}
                            />
                        </Box>
                    )}
                </Grid>
                <Grid item md={3} lg={2} sx={{ display: { xs: 'none', md: 'block' } }}>
                    <ActionBox
                        sx={{ display: { md: 'none', lg: 'block' } }}
                        items={actionItems}
                    />
                    {!headerCollapsed && safetyClass && (
                        <SafetyClassBox safetyClass={safetyClass} />
                    )}
                    {!headerCollapsed && (
                        <GlobalWarmingPotential
                            marginTop={6}
                            tonsAmount={globalWarmingPotential}
                        />
                    )}
                    {pwaActionItems.length > 0 && (
                        <Box sx={{ display: { md: 'block', lg: 'none' }, mt: 2 }}>
                            <ActionBox items={pwaActionItems} />
                        </Box>
                    )}
                </Grid>
            </Grid>

            <Grid container spacing={2} pb={{ xs: 6, lg: 0 }}>
                <Grid item xs={12} md={12} lg={12}>
                    {
                        primaryTabsMemo && (
                            <Tabs
                                activeTab={activeTabPrimary}
                                onhandleChange={(_, newValue) => {
                                    if (newValue === ((!isSubsystem(assetType) && data) ? 6 : 5)) {
                                        setIsFocusText(true);
                                    }
                                    setActiveTabPrimary(newValue);
                                    hotjar.stateChange(document.URL + '#' + primaryTabsMemo.label[newValue - 1]);
                                }}
                                labels={primaryTabsMemo.label}
                                tabElements={primaryTabsMemo.children}
                                tabCustomStyle={{ ml: { xs: 0, lg: "25px" } }}
                                tabPanelCustomStyle={{ ml: { xs: 0, lg: "8px" } }}
                            />
                        )
                    }
                </Grid>
            </Grid>

            <ReweighModal
                inventoryDetailsPage
                open={reweighState.open}
                header={`Reweigh Cylinder #${data.id}`}
                onSubmit={handleReweighSubmit}
                closeModal={() => setReweighState({ open: false })}
                state={{ ...reweighState, maxGasCapacity: data.maxGasCapacity }}
                setReweighState={setReweighState}
                reweighModalButtonDisabled={reweighModalButtonDisabled}
            />

            <AliasFormModal
                open={openAliasModal.isVisible}
                closeModal={() => onAliasModalStateChange()}
                assetId={assetID}
                alias={openAliasModal.alias}
                onSaveSuccess={(data) => onAliasTransactionComplete(data)}
                onDeleteSuccess={(id) => onAliasDeleteComplete(id)}
            />

            <AddSensorModal
                open={openSensorModal}
                assetId={assetID}
                assetName={name}
                closeModal={() => setOpenSensorModal(false)}
            />

            <DecommissionModal
                open={decommissionState.open}
                header={`Decommission ${name}`}
                onSubmit={handleDecommissionSubmit}
                closeModal={() => resetDecommissionState()}
                state={decommissionState}
                setDecommissionState={setDecommissionState}
                decommissionButtonDisabled={decommissionButtonDisabled}
            />

            <SystemRegistrationModal
                open={systemRegistrationState.open}
                onSubmit={handleSystemRegistrationSubmit}
                closeModal={() => resetSystemRegistrationState()}
                state={systemRegistrationState}
                setState={setSystemRegistrationState}
                isLoading={systemRegistrationState.saving}
            />
            {
                renderTransferGasToCylinder && (
                    <AssetDetailsCylinderGasTransfer
                        assetName={name}
                        assetId={assetID}
                        materialTypeName={data.materialTypeName}
                        currentGasWeightLBS={data.currentGasWeightLBS}
                        open={showCylinderTransferDialog}
                        onSucessfulTransfer={() => {
                            fetchData()
                            setCylinderTransferTableRefreshToggle(crypto.randomUUID())
                        }}
                        onHandleClose={() => setShowCylinderTransferDialog(false)}
                    />
                )
            }
        </>
    );
};

AssetDetails.propTypes = {
    match: PropTypes.object.isRequired,
};

export default AssetDetails;